import { cn } from "../../lib/cn";
import {
  buildMiniGrid,
  monthLabel,
  WEEKDAY_MINI_HEADERS,
  type MonthCursor,
} from "../../lib/journal";

export interface DayPnl {
  net_pnl_amount: string;
  trade_count: number;
}

interface MiniCalendarProps {
  cursor: MonthCursor;
  dayPnl: Map<string, DayPnl>;
  noteDays: Set<string>;
  selectedDay: string | null;
  today: string;
  onPrev: () => void;
  onNext: () => void;
  onSelectDay: (isoDate: string) => void;
}

// Per-cell tint, mirroring the mockup's ".cs-cell" heatmap: real P&L days tint
// green/red by net sign, out-of-month cells fade away, the rest stay surface.
function cellTint(inMonth: boolean, pnl: DayPnl | undefined): string {
  if (!inMonth) return "border-transparent bg-transparent text-on-surface-muted/40";
  if (pnl === undefined) {
    return "border-outline-variant bg-surface-container-high text-on-surface-muted";
  }
  const amount = Number(pnl.net_pnl_amount);
  if (amount > 0) return "border-success bg-success-soft text-success";
  if (amount < 0) return "border-danger bg-danger-soft text-danger";
  return "border-outline-variant bg-surface-container-high text-on-surface-muted";
}

// Compact sign-only P&L for a calendar cell (e.g. "+1.2k", "-380"). Avoids any
// money math beyond reading the server amount and choosing a unit.
function compactPnl(amount: number): string {
  const sign = amount > 0 ? "+" : amount < 0 ? "-" : "";
  const abs = Math.abs(amount);
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}k`;
  return `${sign}${Math.round(abs)}`;
}

// The sticky calendar rail's month heatmap (mockup layout 4, ".cs-grid"). Days
// tint by the real net P&L from the analytics calendar; days carrying a journal
// note get an accent dot; the selected day shows the accent outline.
export function MiniCalendar({
  cursor,
  dayPnl,
  noteDays,
  selectedDay,
  today,
  onPrev,
  onNext,
  onSelectDay,
}: MiniCalendarProps) {
  const cells = buildMiniGrid(cursor);
  return (
    <div className="rounded-card border border-outline-variant bg-surface-container p-3">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous month"
          onClick={onPrev}
          className="grid h-6 w-6 place-items-center rounded-control text-on-surface-muted hover:bg-surface-container-low"
        >
          &lsaquo;
        </button>
        <span className="font-display text-lg text-on-surface" data-testid="journal-month-label">
          {monthLabel(cursor)}
        </span>
        <button
          type="button"
          aria-label="Next month"
          onClick={onNext}
          className="grid h-6 w-6 place-items-center rounded-control text-on-surface-muted hover:bg-surface-container-low"
        >
          &rsaquo;
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1" aria-hidden="true">
        {WEEKDAY_MINI_HEADERS.map((label, i) => (
          <span
            key={i}
            className="pb-0.5 text-center text-[9px] font-bold text-outline"
          >
            {label}
          </span>
        ))}
      </div>

      <div role="grid" aria-label="Journal month heatmap" className="grid grid-cols-7 gap-1">
        {cells.map((cell) => {
          const pnl = cell.inMonth ? dayPnl.get(cell.isoDate) : undefined;
          const isSelected = cell.isoDate === selectedDay;
          const isToday = cell.inMonth && cell.isoDate === today;
          const hasNote = cell.inMonth && noteDays.has(cell.isoDate);
          if (!cell.inMonth) {
            return (
              <span
                key={cell.isoDate}
                aria-hidden="true"
                className="flex aspect-square items-center justify-center text-[10px] text-on-surface-muted/40"
              >
                {cell.day}
              </span>
            );
          }
          return (
            <button
              key={cell.isoDate}
              type="button"
              role="gridcell"
              data-testid={`journal-cal-cell-${cell.isoDate}`}
              aria-label={`${cell.isoDate}${hasNote ? ", has notes" : ""}`}
              aria-pressed={isSelected}
              onClick={() => onSelectDay(cell.isoDate)}
              className={cn(
                "relative flex aspect-square flex-col items-center justify-center gap-px rounded-[7px] border transition-[outline]",
                cellTint(cell.inMonth, pnl),
                isToday && "ring-1 ring-inset ring-outline",
                isSelected && "outline outline-2 -outline-offset-2 outline-primary",
              )}
            >
              <span className="text-[10px] font-semibold leading-none">{cell.day}</span>
              {pnl !== undefined ? (
                <span className="font-mono text-[9px] font-bold leading-none">
                  {compactPnl(Number(pnl.net_pnl_amount))}
                </span>
              ) : null}
              {hasNote ? (
                <span
                  aria-hidden="true"
                  className="absolute right-1 top-1 h-1 w-1 rounded-full bg-primary"
                />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
